import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7d1f12de"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  class: "calendar",
  "data-ts": "app-calendar"
}
const _hoisted_2 = { class: "calendar__header" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "bx bx-chevron-left text-lg" }, null, -1))
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = {
  class: "calendar__header__month",
  "data-ts": "calendar-month"
}
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "bx bx-chevron-right text-lg" }, null, -1))
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = {
  class: "weekdays",
  role: "none"
}
const _hoisted_9 = {
  class: "days-grid",
  role: "none"
}
const _hoisted_10 = ["data-ts", "onClick", "onKeydown"]
const _hoisted_11 = { class: "days-grid__calendar-day__number" }
const _hoisted_12 = { class: "calendar__controls" }

import { ref, computed, watch, onMounted } from 'vue';
import dayjs from 'dayjs';
import { ToggleSwitch, Btn } from '@ds/components';
import {
	getMonthDays,
	createDaysForPreviousMonth,
	createDaysForNextMonth,
	normalizeDate,
} from './calendarUtils';

/**
 * Components uses dayjs library for formatting Strings to Dates. Safari and Firefox
 * can not process all date formats like Chrome and Edge so 'YYYY/MM/DD' is used as
 * the most compatible one.
 */

type Day = { date: string; dayOfMonth: number; isCurrentMonth: boolean };
type DisabledDates = { from: string; to: string };


export default /*#__PURE__*/_defineComponent({
  __name: 'CalendarMonth',
  props: {
    hasRange: { type: Boolean },
    disabledDates: {},
    predefinedStart: {},
    predefinedEnd: {},
    isShowingControls: { type: Boolean },
    isShowingRangeToggle: { type: Boolean }
  },
  emits: [
	'startDaySelected',
	'endDaySelected',
	'clearDates',
	'toggleHasRange',
	'applyUserSelection',
],
  setup(__props: any, { emit: __emit }) {

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/**
 * Disabled dates, if one on them is null the other covers all dates from or up to that
 * date respectively. (if { from: Date, to: null } then all dates from disabledDates from
 * will be disabled)
 * @property {Object} disabledDates -- Can be passed as a prop to disable certain dates.
 * @example
 * disabledDates: {
 *     from: Date,
 *     to: Date
 * }
 * @property {String} predefinedStart -- Could be passed to show a preselected start day
 * @property {String} predefinedEnd -- Could be passed to show a preselected end day
 */

const emit = __emit;

const props = __props;

const monthDays = ref();
const previousMonthDays = ref<Day[]>([]);
const nextMonthDays = ref<Day[]>([]);
const calendarDays = ref<Day[]>([]);
const today = ref();
const selectedMonth = ref();
const selectedStartDay = ref<string | null>(null);
const selectedEndDay = ref<string | null>(null);

const computedSelectedMonth = computed(() => {
	return dayjs(selectedMonth.value).format('MMMM YYYY');
});

watch(monthDays, () => {
	calendarDays.value = [...previousMonthDays.value, ...monthDays.value, ...nextMonthDays.value];
});

onMounted(() => {
	/**
	 * Component initializes with translated titles for weekdays.
	 * Current month, year and day. Selected start and end days are
	 * also set if provided from props.
	 *
	 * The arrays of days from current month
	 * with visible days from previous and next month
	 * are destructured in:
	 * @property {Array} calendarDays -- all days to be displayed.
	 *
	 * @function isDateDisabled -- Checks if date falls in the period of disabledDates
	 * @param {Date} date
	 *
	 * NAVIGATION:
	 * @function createCalendar -- re-initiates the calculation of days
	 * @function getMonth -- sets selectedMonth
	 * @param {String} type -- if next then selectedMonth = selectedMonth + 1 otherwise selectedMonth - 1
	 * and calls createCalendar function.
	 *
	 * @function onSelectDay -- first click set selectedStartDay, subsequent clicks set selectedEndDay
	 * @property {Date} selectedStartDay
	 * @property {Date} selectedEndDay
	 * @function clearDates -- sets selectedStartDay & selectedEndDay to null
	 * and emits custom event 'clearDates'
	 */

	// Init calendar with current month and year, unless startDate is passed:
	const initYear = props.predefinedStart
		? dayjs(props.predefinedStart).format('YYYY')
		: dayjs().format('YYYY');

	const initMonth = props.predefinedStart
		? dayjs(props.predefinedStart).format('M')
		: dayjs().format('M');

	today.value = dayjs().format('YYYY/MM/DD');

	// Get calendar visible days
	monthDays.value = getMonthDays(initYear, initMonth);
	previousMonthDays.value = createDaysForPreviousMonth(initYear, initMonth, monthDays.value);
	nextMonthDays.value = createDaysForNextMonth(initYear, initMonth, monthDays.value);
	calendarDays.value = [...previousMonthDays.value, ...monthDays.value, ...nextMonthDays.value];

	// Set selected
	selectedMonth.value = dayjs(`${initYear}/${initMonth}`);
	selectedStartDay.value = props.predefinedStart || null;
	selectedEndDay.value = props.predefinedEnd || null;
});

const createCalendar = (year: string, month: string) => {
	selectedMonth.value = dayjs(`${year}/${month}/1`);
	monthDays.value = getMonthDays(year, month);
	previousMonthDays.value = createDaysForPreviousMonth(year, month, monthDays.value);
	nextMonthDays.value = createDaysForNextMonth(year, month, monthDays.value);
};

const getMonth = (type: string) => {
	selectedMonth.value =
		type === 'next'
			? dayjs(selectedMonth.value).add(1, 'month')
			: dayjs(selectedMonth.value).subtract(1, 'month');

	createCalendar(selectedMonth.value.format('YYYY'), selectedMonth.value.format('M'));
};

const isDateDisabled = (date: string) => {
	const normalizeDateFrom = normalizeDate(props.disabledDates?.from);
	const normalizeDateTo = normalizeDate(props.disabledDates?.to);

	const hasBothLimits = props.disabledDates?.from && props.disabledDates?.to;

	const hasFromLimitAndIsLarger =
		!props.disabledDates?.to && props.disabledDates?.from && date >= normalizeDateFrom;

	const hasToLimitAndIsSmaller =
		props.disabledDates?.to && !props.disabledDates?.from && date < normalizeDateTo;

	const hasBothLimitsAndIsBetween =
		hasBothLimits && date <= normalizeDateTo && date >= normalizeDateFrom;

	return hasFromLimitAndIsLarger || hasToLimitAndIsSmaller || hasBothLimitsAndIsBetween;
};

const getDayClass = (day: Day) => {
	return [
		{ 'days-grid__calendar-day--disabled': isDateDisabled(day.date) },
		{ 'days-grid__calendar-day--not-current': !day.isCurrentMonth },
	];
};

const getPeriodClass = (day: Day) => {
	const normalizedStartDay = normalizeDate(selectedStartDay.value);
	const normalizedEndDay = normalizeDate(selectedEndDay.value);

	const isBetweenStartAndEnd =
		selectedEndDay.value && day.date < normalizedEndDay && day.date > normalizedStartDay;

	const isStartDay = day.date === normalizedStartDay;
	const isEndDay = day.date === normalizedEndDay;
	const isDayToday = day.date === today.value && day.isCurrentMonth;

	return [
		{ 'days-grid__calendar-day--period': isBetweenStartAndEnd },
		{ 'days-grid__calendar-day--selected': isStartDay },
		{ 'days-grid__calendar-day--selected days-grid__calendar-day--selected--end': isEndDay },
		{ 'days-grid__calendar-day--today': isDayToday },
	];
};

const onSelectDay = (day: Day) => {
	const selectedDay = day.dayOfMonth;
	const currentMonth = dayjs(day.date).format('MM');
	const currentYear = dayjs(day.date).format('YYYY');
	const selectedDate = normalizeDate(`${currentYear}/${currentMonth}/${selectedDay}`);

	if (selectedStartDay.value && !isDateDisabled(selectedDate)) {
		if (dayjs(selectedDate) <= dayjs(selectedStartDay.value) || !props.hasRange) {
			/** if second selection is an earlier date than the first,
			 * or is-single-date true then
			 * reset dates and set startDate again */
			selectedEndDay.value = null;
			emit('clearDates');

			selectedStartDay.value = selectedDate;
			emit('startDaySelected', selectedStartDay.value);
		} else {
			/** Otherwise emit the selected end day */
			selectedEndDay.value = selectedDate;
			emit('endDaySelected', selectedEndDay.value);
		}
	} else if (!selectedStartDay.value && !isDateDisabled(selectedDate)) {
		selectedStartDay.value = selectedDate;
		emit('startDaySelected', selectedStartDay.value);
	}
};

const clearUserSelection = () => {
	selectedStartDay.value = null;
	selectedEndDay.value = null;
};

const toggleHasRange = (hasRange: boolean) => {
	clearUserSelection();
	emit('toggleHasRange', hasRange);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _createElementVNode("button", {
        id: "previous-month-selector",
        "aria-label": "Previous month",
        name: "previousMonthBtn",
        class: "calendar__header__btn",
        type: "button",
        "data-ts": "previous-month-btn",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (getMonth('previous')))
      }, _hoisted_4),
      _createElementVNode("div", _hoisted_5, _toDisplayString(computedSelectedMonth.value), 1),
      _createElementVNode("button", {
        id: "next-month-selector",
        "aria-label": "Next month",
        name: "nextMonthBtn",
        class: "calendar__header__btn",
        type: "button",
        "data-ts": "next-month-btn",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (getMonth('next')))
      }, _hoisted_7)
    ]),
    _createElementVNode("ol", _hoisted_8, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(WEEKDAYS, (weekday, index) => {
        return _createElementVNode("li", {
          key: index,
          class: "weekdays__day",
          role: "none"
        }, _toDisplayString(weekday), 1)
      }), 64))
    ]),
    _createElementVNode("ul", _hoisted_9, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarDays.value, (day, index) => {
        return (_openBlock(), _createElementBlock("li", {
          key: `day-btn-${index}`,
          class: _normalizeClass([[getDayClass(day), getPeriodClass(day)], "days-grid__calendar-day"]),
          tabindex: "0",
          "data-ts": `day-btn-${index}`,
          onClick: ($event: any) => (onSelectDay(day)),
          onKeydown: _withKeys(_withModifiers(($event: any) => (onSelectDay(day)), ["prevent"]), ["enter"])
        }, [
          _createElementVNode("div", _hoisted_11, _toDisplayString(day.dayOfMonth), 1)
        ], 42, _hoisted_10))
      }), 128))
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_12, [
      (_ctx.isShowingRangeToggle)
        ? (_openBlock(), _createBlock(_unref(ToggleSwitch), {
            key: 0,
            style: {"flex-grow":"2"},
            label: "Single date",
            "label-right": "",
            "data-ts": "rangeToggle",
            "is-checked": !_ctx.hasRange,
            onChange: toggleHasRange
          }, null, 8, ["is-checked"]))
        : _createCommentVNode("", true),
      (_ctx.isShowingControls)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _withDirectives(_createVNode(_unref(Btn), {
              label: "Clear",
              size: "small",
              variant: "secondary",
              class: "calendar__btn mr-2",
              onClick: clearUserSelection
            }, null, 512), [
              [_vShow, _ctx.hasRange ? selectedStartDay.value && selectedEndDay.value : selectedStartDay.value]
            ]),
            _createVNode(_unref(Btn), {
              label: "Apply",
              size: "small",
              class: "calendar__btn",
              disabled: _ctx.hasRange ? !selectedStartDay.value || !selectedEndDay.value : !selectedStartDay.value,
              onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('applyUserSelection'))),
              "data-ts": "date-picker-apply"
            }, null, 8, ["disabled"])
          ], 64))
        : _createCommentVNode("", true)
    ], 512), [
      [_vShow, _ctx.isShowingControls || _ctx.isShowingRangeToggle]
    ])
  ]))
}
}

})